import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import BackgroundColor from '@tiptap/extension-text-style/background-color';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import { Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import MentionAutocomplete, { buildCitationHTML, citationToken } from './MentionAutocomplete';
import RichFormatPill from './RichFormatPill';
import { imageSourceFromTransfer, RESPONSE_IMAGE_TYPES } from './responseImageUpload';
import { hasCitationToken, tokenTextToHTML } from './citationClipboard';

// A markdown line holding only an image id is where the report places that image
// (data_extractor _ANCHOR_LINE). The editor shows it as a locked "Figure N" block so
// the id can't be half-edited, and writes it back as the same bare line.
const IMAGE_ANCHOR_LINE_RE = /^\s*\[(agent_\d+_image_\d+_\d+)\]\s*$/;

const ImageAnchorNode = TiptapNode.create({
  name: 'imageAnchor',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  // Display-only attributes (thumb, caption, isNew): the markdown keeps only the id.
  addAttributes() {
    return {
      imageId: { default: '' },
      label: { default: 'Figure' },
      thumb: { default: '' },
      caption: { default: '' },
      isNew: { default: false },
    };
  },

  parseHTML() {
    return [{
      tag: 'div[data-image-anchor]',
      getAttrs: (node) => ({
        imageId: node.getAttribute('data-image-anchor') || '',
        label: node.getAttribute('data-label') || 'Figure',
        thumb: node.getAttribute('data-thumb') || '',
        caption: node.getAttribute('data-caption') || '',
        isNew: node.getAttribute('data-new') === 'true',
      }),
    }];
  },

  renderHTML({ node }) {
    const { imageId, label, thumb, caption, isNew } = node.attrs;
    return [
      'div',
      mergeAttributes({
        'data-image-anchor': imageId,
        'data-label': label,
        'data-thumb': thumb,
        'data-caption': caption,
        'data-new': isNew ? 'true' : 'false',
        class: 'genbi-image-anchor',
        contenteditable: 'false',
        title: 'Figure placed here in the report — drag to move, select and Delete to remove',
      }),
      thumb
        ? ['img', { class: 'genbi-image-anchor-thumb', src: thumb, alt: '' }]
        : ['span', { class: 'genbi-image-anchor-icon' }, ['i', { class: 'bi bi-image' }]],
      ['span', { class: 'genbi-image-anchor-text' },
        ['span', { class: 'genbi-image-anchor-label' },
          label || 'Figure',
          ...(isNew ? [['span', { class: 'genbi-image-anchor-badge' }, 'New']] : []),
        ],
        ['span', { class: 'genbi-image-anchor-caption' }, caption || 'No subtitle'],
      ],
    ];
  },
});

const escapeAttr = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

// imageLabels values may be a plain label or { label, thumb, caption, isNew }.
const anchorMeta = (entry) => (typeof entry === 'string' ? { label: entry } : (entry || {}));

const CitationNode = TiptapNode.create({
  name: 'citation',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      label: { default: '' },
      kind: { default: 'doc' },
      // The source token, kept so the chip survives a TipTap round trip with its id.
      dataRef: { default: '' },
      class: {
        default: 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.ev-message-card6',
        getAttrs: (node) => ({
          label: node.textContent || '',
          kind: 'doc',
          dataRef: node.getAttribute('data-ref') || '',
          class: node.getAttribute('class') || 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg',
        }),
      },
      {
        tag: 'span.ev-message-card7',
        getAttrs: (node) => ({
          label: node.textContent || '',
          kind: 'ref',
          dataRef: node.getAttribute('data-ref') || '',
          class: node.getAttribute('class') || 'ev-message-card7 m-0 mx-1 ev-btn-xsmall ev-bold-reg',
        }),
      },
    ];
  },

  renderHTML({ node }) {
    const cls = node.attrs.class || (node.attrs.kind === 'ref'
      ? 'ev-message-card7 m-0 mx-1 ev-btn-xsmall ev-bold-reg'
      : 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg');
    const attrs = { class: cls };
    if (node.attrs.dataRef) attrs['data-ref'] = node.attrs.dataRef;
    return ['span', mergeAttributes(attrs), node.attrs.label || ''];
  },
});

const normaliseClassAttr = (html) =>
  String(html || '').replace(/\sclassName=/gi, ' class=');

const renderInlineMarkdown = (value) => {
  let html = normaliseClassAttr(value);
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|[\s(>])_(?!\s)(.+?)_(?=[\s.,;:!?)\]<]|$)/g, '$1<em>$2</em>');
  html = html.replace(/\^([^\^\s]+)\^/g, '<sup>$1</sup>');
  html = html.replace(/(?<!~)~([^~\s]+)~(?!~)/g, '<sub>$1</sub>');
  return html;
};

const COLOR_DIV_OPEN_RE = /^<!--PRESERVE_BOUNDARY-->\s*<div\s+style="color:\s*([^"]+)"[^>]*>$/i;
const COLOR_DIV_OPEN_PLAIN_RE = /^<div\s+style="color:\s*([^"]+)"[^>]*>$/i;
const COLOR_DIV_CLOSE_RE = /^<\/div>$/i;

// An amend-highlight span parked in front of a list marker ("<span ..>- x") would make the
// line a paragraph with a literal "-". View mode hoists it (GenbiResponseComponent
// LIST_MARKER_SPAN_REGEX); the editor does the same so both show a list.
const LIST_MARKER_SPAN_RE = /^([ \t]*)(<span\b[^>]*>)[ \t]*([-*+]|\d+[.)])[ \t]+/gm;
const LIST_ITEM_RE = /^([ \t]*)([-*+]|\d+[.)])[ \t]+(.+)$/;

const indentWidth = (value) => value.replace(/\t/g, '    ').length;

const wrapInline = (html, colorHex) => {
  if (!colorHex) return html;
  return `<span style="color:${colorHex}">${html}</span>`;
};

// items: [{ indent, ordered, html }] -> nested <ul>/<ol>. A deeper indent opens a list
// inside the previous item, so "  - x" under "- y" stays a sub-bullet.
const buildListHtml = (items) => {
  let html = '';
  const stack = [];
  items.forEach((item) => {
    const tag = item.ordered ? 'ol' : 'ul';
    while (stack.length && item.indent < stack[stack.length - 1].indent) {
      html += `</li></${stack.pop().tag}>`;
    }
    const top = stack[stack.length - 1];
    if (!top || item.indent > top.indent) {
      html += `<${tag}>`;
      stack.push({ indent: item.indent, tag });
    } else {
      html += '</li>';
      if (top.tag !== tag) {
        html += `</${top.tag}><${tag}>`;
        top.tag = tag;
      }
    }
    html += `<li><p>${item.html}</p>`;
  });
  while (stack.length) html += `</li></${stack.pop().tag}>`;
  return html;
};

const markdownToHtml = (markdown, imageLabels = {}) => {
  const lines = String(markdown || '')
    .replace(/\r\n?/g, '\n')
    .replace(LIST_MARKER_SPAN_RE, (match, indent, span, marker) => `${indent}${marker} ${span}`)
    .split('\n');
  const out = [];
  let activeColor = null;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    const anchor = line.match(IMAGE_ANCHOR_LINE_RE);
    if (anchor) {
      const meta = anchorMeta(imageLabels[anchor[1]]);
      out.push(
        `<div data-image-anchor="${escapeAttr(anchor[1])}" data-label="${escapeAttr(meta.label || 'Figure')}"`
        + ` data-thumb="${escapeAttr(meta.thumb || '')}" data-caption="${escapeAttr(meta.caption || '')}"`
        + ` data-new="${meta.isNew ? 'true' : 'false'}"></div>`
      );
      continue;
    }

    const colorOpen = line.match(COLOR_DIV_OPEN_RE) || line.match(COLOR_DIV_OPEN_PLAIN_RE);
    if (colorOpen) {
      activeColor = colorOpen[1];
      continue;
    }
    if (COLOR_DIV_CLOSE_RE.test(line.trim())) {
      activeColor = null;
      continue;
    }

    if (!line.trim()) {
      out.push('<p></p>');
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 6);
      out.push(`<h${level}>${wrapInline(renderInlineMarkdown(heading[2]), activeColor)}</h${level}>`);
      continue;
    }

    if (LIST_ITEM_RE.test(line)) {
      const items = [];
      let j = i;
      while (j < lines.length) {
        const item = lines[j].match(LIST_ITEM_RE);
        if (item) {
          items.push({
            indent: indentWidth(item[1]),
            ordered: !/^[-*+]$/.test(item[2]),
            html: wrapInline(renderInlineMarkdown(item[3]), activeColor),
          });
          j += 1;
          continue;
        }
        // Blank lines between items (a loose list) don't end the list.
        if (!lines[j].trim()) {
          let k = j;
          while (k < lines.length && !lines[k].trim()) k += 1;
          if (k < lines.length && LIST_ITEM_RE.test(lines[k])) {
            j = k;
            continue;
          }
        }
        break;
      }
      i = j - 1;
      out.push(buildListHtml(items));
      continue;
    }

    out.push(`<p>${wrapInline(renderInlineMarkdown(line), activeColor)}</p>`);
  }

  return out.join('');
};

const textOf = (node) => (node.textContent || '').replace(/\u200B/g, '');

const normaliseHex = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const shortHex = raw.match(/^#?([0-9a-f]{3})$/i);
  if (shortHex) {
    return shortHex[1].split('').map((c) => c + c).join('').toUpperCase();
  }
  const hex = raw.match(/^#?([0-9a-f]{6})$/i);
  if (hex) return hex[1].toUpperCase();
  const rgb = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgb) {
    return [rgb[1], rgb[2], rgb[3]]
      .map((n) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }
  return null;
};

const styleDecls = (el) => {
  const color = normaliseHex(el.style?.color);
  const bg = normaliseHex(el.style?.backgroundColor);
  const decls = [];
  if (color) decls.push(`color:#${color};`);
  if (bg) decls.push(`background-color:#${bg};`);
  return decls.join('');
};

const serializeInlineNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return textOf(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node;
  const tag = el.tagName.toLowerCase();
  const children = Array.from(el.childNodes).map(serializeInlineNode).join('');

  if (tag === 'br') return '\n';
  if (tag === 'span' && /\bev-message-card[67]\b/.test(el.className || '')) {
    return buildCitationHTML({
      kind: /\bev-message-card7\b/.test(el.className || '') ? 'ref' : 'doc',
      label: textOf(el),
      ref: el.getAttribute('data-ref') || '',
    });
  }
  if (tag === 'strong' || tag === 'b') {
    const trimmed = children.trimStart();
    const lead = children.slice(0, children.length - trimmed.length);
    const core = trimmed.trimEnd();
    const trail = trimmed.slice(core.length);
    return core ? `${lead}**${core}**${trail}` : children;
  }
  if (tag === 'em' || tag === 'i') {
    const trimmed = children.trimStart();
    const lead = children.slice(0, children.length - trimmed.length);
    const core = trimmed.trimEnd();
    const trail = trimmed.slice(core.length);
    return core ? `${lead}_${core}_${trail}` : children;
  }
  if (tag === 'u') return `<u>${children}</u>`;
  if (tag === 'sup') {
    return /^[^\^\s]+$/.test(children) ? `^${children}^` : `<sup>${children}</sup>`;
  }
    if (tag === 'sub') {
    return /^[^~\s]+$/.test(children) ? `~${children}~` : `<sub>${children}</sub>`;
  }
  if (tag === 'a') {
    const href = el.getAttribute('href');
    return href ? `<a href="${href}">${children}</a>` : children;
  }
  if (tag === 'span' || tag === 'mark') {
    const decls = styleDecls(el);
    return decls ? `<span style="${decls}">${children}</span>` : children;
  }

  return children;
};

// A nested list is written under its item, indented past the parent marker ("- " -> 2
// spaces, "1. " -> 3), so the sub-bullets keep their level on save.
const serializeList = (el, indent = '') => {
  const ordered = el.tagName.toLowerCase() === 'ol';
  const lines = [];
  Array.from(el.children)
    .filter((child) => child.tagName?.toLowerCase() === 'li')
    .forEach((li, idx) => {
      const marker = ordered ? `${idx + 1}.` : '-';
      const nested = [];
      const parts = [];
      Array.from(li.childNodes).forEach((child) => {
        const childTag = child.tagName?.toLowerCase();
        if (childTag === 'ul' || childTag === 'ol') nested.push(child);
        else parts.push(serializeInlineNode(child));
      });
      lines.push(`${indent}${marker} ${parts.join('').trim()}`);
      nested.forEach((list) => lines.push(serializeList(list, indent + ' '.repeat(marker.length + 1))));
    });
  return lines.join('\n');
};

const serializeBlockNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return textOf(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node;
  const tag = el.tagName.toLowerCase();
  const inline = () => Array.from(el.childNodes).map(serializeInlineNode).join('');

  if (el.hasAttribute('data-image-anchor')) {
    const imageId = el.getAttribute('data-image-anchor');
    return imageId ? `[${imageId}]\n\n` : '';
  }
  if (/^h[1-6]$/.test(tag)) return `${'#'.repeat(Number(tag[1]))} ${inline()}\n\n`;
  if (tag === 'ul' || tag === 'ol') return `${serializeList(el)}\n\n`;
  if (tag === 'p' || tag === 'div') return `${inline()}\n\n`;
  if (tag === 'br') return '\n';

  return `${inline()}\n\n`;
};

const htmlToReportMarkdown = (html) => {
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  return Array.from(doc.body.firstChild.childNodes)
    .map(serializeBlockNode)
    .join('')
    .replace(/\u200B/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const BlockToolbarButton = ({ active = false, label, title, onClick, children }) => (
  <button
    type="button"
    className={'genbi-agent-response-toolbar-btn' + (active ? ' active' : '')}
    title={title}
    aria-label={label}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
  >
    {children}
  </button>
);

const AgentResponseEditor = ({
  value,
  onChange,
  docs = [],
  refs = [],
  onNeedReferences,
  onCreateReference,
  refsLoading = false,
  // Optional. (file) => Promise<{ imageId, label } | null>. When given, an image pasted
  // or dropped into the editor is handed over (the caller shows the popup and uploads)
  // and a figure anchor is inserted where it was pasted.
  onPasteImage,
  // { [image_id]: 'Figure N' } — the label shown on each figure anchor block.
  imageLabels = {},
  // Optional. Called when the text gets focus, so the host knows the text (not a table
  // cell) is where the "Insert image" button should put the next image.
  onBodyFocus,
  // Optional. (source) => boolean. The "Insert image" button offers the picked image
  // here first; true means the host placed it (in the table cell last edited).
  routePickedImage,
}) => {
  const editorDomRef = useRef(null);
  const latestValueRef = useRef(value || '');
  const onPasteImageRef = useRef(onPasteImage);
  onPasteImageRef.current = onPasteImage;
  const imageLabelsRef = useRef(imageLabels);
  imageLabelsRef.current = imageLabels;
  const onBodyFocusRef = useRef(onBodyFocus);
  onBodyFocusRef.current = onBodyFocus;
  const docsRef = useRef(docs);
  docsRef.current = docs;
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [tick, setTick] = useState(0);
  const [pillRect, setPillRect] = useState(null);
  const [pillMarks, setPillMarks] = useState({});
  const [pillOnCitation, setPillOnCitation] = useState(false);

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
    }),
    Underline,
    TextStyle,
    Color,
    BackgroundColor,
    Superscript,
    Subscript,
    Link.configure({ openOnClick: false }),
    CitationNode,
    ImageAnchorNode,
  ], []);

  // Hand a pasted/dropped image ({ load }) to the caller, then drop the anchor at `pos`.
  const takeImage = useCallback((source, pos) => {
    const handler = onPasteImageRef.current;
    if (!handler || !source) return false;
    Promise.resolve(source.load())
      .catch((e) => {
        window.alert(e?.message || 'The pasted image could not be read.');
        return null;
      })
      .then((file) => (file ? handler(file) : null))
      .then((result) => {
        const instance = editorRef.current;
        if (!result?.imageId || !instance || instance.isDestroyed) return;
        const at = Math.min(Math.max(0, pos), instance.state.doc.content.size);
        instance
          .chain()
          .focus()
          .insertContentAt(at, {
            type: 'imageAnchor',
            attrs: {
              imageId: result.imageId,
              label: result.label || 'Figure',
              thumb: result.thumb || '',
              caption: result.caption || '',
              isNew: !!result.isNew,
            },
          })
          .run();
      })
      .catch((e) => console.error('Pasted image was not inserted', e));
    return true;
  }, []);

  const editor = useEditor({
    extensions,
    content: markdownToHtml(value, imageLabels),
    editorProps: {
      attributes: {
        class: 'genbi-agent-response-editor-surface',
        spellcheck: 'true',
      },
      // A copied pill goes out as its [doc_id_N] / [ar_id_N] token in plain text, so a
      // subtitle or footer textarea keeps the citation when it is pasted there.
      clipboardTextSerializer: (slice) => slice.content.textBetween(
        0,
        slice.content.size,
        '\n\n',
        (leaf) => {
          if (leaf.type.name === 'citation') return leaf.attrs.dataRef || leaf.attrs.label || '';
          return leaf.type.name === 'hardBreak' ? '\n' : '';
        },
      ),
      handlePaste: (view, event) => {
        if (onPasteImageRef.current) {
          const source = imageSourceFromTransfer(event.clipboardData);
          if (source) {
            event.preventDefault();
            return takeImage(source, view.state.selection.to);
          }
        }
        // Plain text with tokens (copied from a subtitle or footer) comes in as pills.
        const data = event.clipboardData;
        const plain = data?.getData('text/plain');
        if (!data || data.getData('text/html') || !hasCitationToken(plain)) return false;
        const instance = editorRef.current;
        if (!instance || instance.isDestroyed) return false;
        event.preventDefault();
        instance.chain().focus().insertContent(tokenTextToHTML(plain, docsRef.current, refsRef.current)).run();
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved || !onPasteImageRef.current) return false;
        const source = imageSourceFromTransfer(event.dataTransfer);
        if (!source) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        return takeImage(source, coords ? coords.pos : view.state.selection.to);
      },
    },
    onUpdate: ({ editor: instance }) => {
      const next = htmlToReportMarkdown(instance.getHTML());
      latestValueRef.current = next;
      onChange(next);
      setTick((n) => n + 1);
    },
    onSelectionUpdate: () => setTick((n) => n + 1),
    onFocus: () => onBodyFocusRef.current?.(),
  });

  useEffect(() => {
    editorDomRef.current = editor?.view?.dom || null;
    editorRef.current = editor || null;
  }, [editor, tick]);

  const refreshPill = useCallback(() => {
    const root = editorDomRef.current;
    const sel = window.getSelection();
    if (!editor || !root || !sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setPillRect(null);
      return;
    }
    if (!root.contains(sel.anchorNode) || !root.contains(sel.focusNode)) {
      setPillRect(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const firstRect = Array.from(range.getClientRects()).find((r) => r.width || r.height);
    const rect = range.getBoundingClientRect();
    const nextRect = firstRect || (rect && (rect.width || rect.height) ? rect : null);
    if (!nextRect) {
      setPillRect(null);
      return;
    }
    setPillOnCitation(editor.state.selection.node?.type.name === 'citation');
    setPillMarks({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      color: editor.getAttributes('textStyle')?.color || null,
      bg: editor.getAttributes('textStyle')?.backgroundColor || null,
    });
    setPillRect(nextRect);
  }, [editor]);

  useEffect(() => {
    if (!editor) return undefined;
    const raf = window.requestAnimationFrame(refreshPill);
    return () => window.cancelAnimationFrame(raf);
  }, [editor, refreshPill, tick]);

  useEffect(() => {
    if (!editor) return undefined;
    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        refreshPill();
      });
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [editor, refreshPill]);

  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    if (next === latestValueRef.current) return;
    latestValueRef.current = next;
    editor.commands.setContent(markdownToHtml(next, imageLabelsRef.current), false);
  }, [editor, value]);

  const insertCitation = useCallback((item, meta = {}) => {
    if (!editor || !item) return;
    const cls = item.kind === 'ref'
      ? 'ev-message-card7 m-0 mx-1 ev-btn-xsmall ev-bold-reg'
      : 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg';
    const queryLength = (meta.query || '').length + 1;
    const to = editor.state.selection.from;
    const from = Math.max(1, to - queryLength);

    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContent({
        type: 'citation',
        attrs: { label: item.label, kind: item.kind, class: cls, dataRef: citationToken(item) },
      })
      .run();
  }, [editor]);

  const runFloatingFormat = useCallback((key, value) => {
    if (!editor) return;
    const chain = editor.chain().focus();
    if (key === 'bold') chain.toggleBold().run();
    else if (key === 'italic') chain.toggleItalic().run();
    else if (key === 'underline') chain.toggleUnderline().run();
    else if (key === 'color') (value ? chain.setColor(value) : chain.unsetColor()).run();
    else if (key === 'bg') {
      (value
        ? chain.setMark('textStyle', { backgroundColor: value })
        : chain.setMark('textStyle', { backgroundColor: null }).removeEmptyTextStyle()
      ).run();
    }
    window.requestAnimationFrame(refreshPill);
  }, [editor, refreshPill]);

  if (!editor) return null;

  return (
    <div className="genbi-agent-response-editor">
      <div className="genbi-agent-response-toolbar" aria-label="Response block formatting">
        <BlockToolbarButton
          active={editor.isActive('heading', { level: 1 })}
          label="Heading 1"
          title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <i className="bi bi-type-h1"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('heading', { level: 2 })}
          label="Heading 2"
          title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <i className="bi bi-type-h2"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('heading', { level: 3 })}
          label="Heading 3"
          title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <i className="bi bi-type-h3"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('bulletList')}
          label="Bullet list"
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <i className="bi bi-list-ul"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('orderedList')}
          label="Numbered list"
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <i className="bi bi-list-ol"></i>
        </BlockToolbarButton>
        {onPasteImage && (
          <>
            <BlockToolbarButton
              label="Insert image"
              title="Insert image where the cursor is — in the text or a table cell (or paste / drop one)"
              onClick={() => imageInputRef.current?.click()}
            >
              <i className="bi bi-image"></i>
            </BlockToolbarButton>
            <input
              ref={imageInputRef}
              type="file"
              accept={RESPONSE_IMAGE_TYPES.join(',')}
              className="d-none"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                const source = { load: async () => file };
                // Cursor last in a table cell: the host puts it there; else at the text cursor
                if (routePickedImage && routePickedImage(source)) return;
                takeImage(source, editor.state.selection.to);
              }}
            />
          </>
        )}
      </div>
      <EditorContent editor={editor} />

      <RichFormatPill
        visible={!!pillRect}
        anchorRect={pillRect}
        clipEl={editorDomRef.current}
        mode="text"
        copyOnly={pillOnCitation}
        marks={pillMarks}
        onToggle={(key) => runFloatingFormat(key)}
        onColor={(color) => runFloatingFormat('color', color)}
        onHighlight={(color) => runFloatingFormat('bg', color)}
        onRemove={(key) => runFloatingFormat(key, null)}
      />

      <MentionAutocomplete
        containerRef={editorDomRef}
        contentEditable
        docs={docs}
        refs={refs}
        onNeedReferences={onNeedReferences}
        onCreateReference={onCreateReference}
        refsLoading={refsLoading}
        onSelectItem={insertCitation}
      />
    </div>
  );
};

export default AgentResponseEditor;
